import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Nav,
  Container,
  Icons,
  Button,
  CartButton,
  Modal,
  Typography,
  Grid,
} from '../';
import { useKeyPress, constants } from '../shared/js';
import styles from './checkout.module.scss';

const { ESCAPE_KEY } = constants;

const Checkout = ({
  loginURL,
  loggedIn,
  dashboard,
  onLogout,
  setLanguage,
  onCartClick,
  onLogoClick,
  navLinks,
}) => {
  const { locale, formatMessage } = useIntl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const escPressed = useKeyPress(ESCAPE_KEY);

  useEffect(() => {
    if (!escPressed || !drawerOpen) return;
    setDrawerOpen(false);
  }, [escPressed, drawerOpen, setDrawerOpen]);

  const handleLogout = () => {
    if (dashboard && !logoutModalOpen) {
      setLogoutModalOpen(true);
      return;
    }
    setLogoutModalOpen(false);
    onLogout();
  };

  return (
    <nav
      className={cn(styles.nav, { [styles.dashboard]: dashboard && loggedIn })}
    >
      <Container flex pvMd>
        <Nav.Logo className={styles.logoContainer} onClick={onLogoClick} />
        {dashboard && loggedIn && <DashboardLinks navLinks={navLinks} />}
        <Nav.Actions alignCenter>
          {dashboard && loggedIn && (
            <>
              <CartButton large onClick={onCartClick} />
              <Button
                id="logoutDashboard"
                underline
                primary
                link
                className={cn(
                  styles.link,
                  styles.underline,
                  styles.thickUnderline,
                  styles.dashboardLink,
                  styles.logoutLink
                )}
                onClick={handleLogout}
              >
                <FormattedMessage id="nav.logout" defaultMessage="Log out" />
              </Button>
            </>
          )}
          <button
            role="button"
            aria-label="open nav"
            id="drawerToggle"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={styles.hamburger}
          >
            <Icons.Hamburger />
          </button>
        </Nav.Actions>
      </Container>
      <div className={cn(styles.drawer, { [styles.open]: drawerOpen })}>
        <div className={styles.drawerHeader}>
          <Button
            primary
            underline
            className={cn(styles.close, {
              [styles.dashboardClose]: dashboard && loggedIn,
            })}
            onClick={() => setDrawerOpen(false)}
          >
            {dashboard && loggedIn && (
              <FormattedMessage id="nav.close" defaultMessage="Close" />
            )}
            <Icons.Close
              className={cn({
                [styles.dashboardCloseIcon]: dashboard && loggedIn,
              })}
            />
          </Button>
        </div>
        {loggedIn ? (
          <Button
            id="logout"
            outline
            primary
            fullWidth
            onClick={handleLogout}
            className={styles.drawerLogin}
          >
            <FormattedMessage id="nav.logOut" defaultMessage="Log out" />
          </Button>
        ) : (
          <Button
            outline
            primary
            fullWidth
            href={loginURL}
            className={styles.drawerLogin}
          >
            <FormattedMessage id="nav.logIn" defaultMessage="Log in" />
          </Button>
        )}
        {dashboard && loggedIn ? (
          <>
            <DashboardLinks drawer navLinks={navLinks} />
            <div className={styles.dashboardDrawerActions}>
              <a
                className={styles.dashboardDrawerAction}
                href="tel:18447666384"
              >
                <Icons.PhoneBottom
                  className={styles.dashboardDrawerActionIcon}
                />
              </a>
              <button
                className={styles.dashboardDrawerAction}
                onClick={setLanguage}
              >
                {locale === 'fr' ? (
                  <Icons.LanguageEn
                    className={styles.dashboardDrawerActionIcon}
                  />
                ) : (
                  <Icons.LanguageFr
                    className={styles.dashboardDrawerActionIcon}
                  />
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.buttonRow}>
              <Button
                underline
                link
                primary
                className={cn(styles.link, styles.underline)}
                href={`${window.location.origin}/shopping_cart`}
              >
                <FormattedMessage id="nav.cart" defaultMessage="Cart" />
              </Button>
              <CartButton onClick={onCartClick} />
            </div>
            <div className={styles.buttonRow}>
              <Button
                aria-label="open claims"
                underline
                primary
                link
                className={cn(styles.link, styles.underline)}
                href={`${window.location.origin}/claims`}
              >
                <FormattedMessage id="nav.claims" defaultMessage="Claims" />
              </Button>
              <Button
                id="claimsOpen"
                className={styles.claimsButton}
                onClick={() => setClaimsOpen(!claimsOpen)}
              >
                {!claimsOpen && <Icons.Plus />}
                {claimsOpen && <Icons.Minus />}
              </Button>
            </div>
            {claimsOpen && (
              <div className={styles.linkColumn}>
                <Button
                  className={cn(styles.claimTypeButton, styles.underline)}
                  link
                  primary
                  underline
                  newTab
                  href="https://sonnet.ca/claims/auto"
                >
                  <FormattedMessage id="nav.autoClaim" defaultMessage="Auto" />
                </Button>
                <Button
                  className={cn(styles.claimTypeButton, styles.underline)}
                  link
                  primary
                  underline
                  newTab
                  href="https://sonnet.ca/claims/home"
                >
                  <FormattedMessage id="nav.homeClaim" defaultMessage="Home" />
                </Button>
                <Button
                  className={cn(styles.claimTypeButton, styles.underline)}
                  underline
                  primary
                  newTab
                  link
                  href="https://www.sonnet.ca/faqs/claims/pet-insurance-claims"
                >
                  <FormattedMessage id="nav.petClaim" defaultMessage="Pet" />
                </Button>
              </div>
            )}
            <div className={cn(styles.bottom, styles.linkColumn)}>
              <Button
                className={cn(styles.link, styles.underline)}
                underline
                primary
                newTab
                link
                href="https://www.sonnet.ca/contact-us"
              >
                <FormattedMessage
                  id="nav.contactUs"
                  defaultMessage="Contact Us"
                />
              </Button>
              <Button
                className={cn(styles.link, styles.underline)}
                underline
                primary
                newTab
                link
                href="https://www.sonnet.ca/faqs"
              >
                <FormattedMessage id="nav.faqs" defaultMessage="FAQs" />
              </Button>
              <Nav.TranslateLink
                className={cn(styles.claimTypeButton, styles.underline)}
                link
                underline
                primary
                onClick={setLanguage}
              />
            </div>
          </>
        )}
      </div>
      {drawerOpen && (
        <div className={styles.overlay} onClick={() => setDrawerOpen(false)} />
      )}
      <Modal
        isOpen={logoutModalOpen}
        onRequestClose={() => setLogoutModalOpen(false)}
        title={formatMessage({
          id: 'nav.logoutTitle',
          defaultMessage: 'Not so fast...are you sure you want to log out?',
        })}
        titleClassName={styles.logoutTitle}
        noUnderline
      >
        <Typography
          bold
          className={cn(styles.logoutTitle, styles.logoutTitleMobile)}
        >
          <FormattedMessage
            id="nav.logoutTitle"
            defaultMessage="Not so fast...are you sure you want to log out?"
          />
        </Typography>
        <Grid>
          <Grid.Row>
            <Grid.Column colMd6>
              <Button
                secondary
                fullWidth
                onClick={() => setLogoutModalOpen(false)}
              >
                <FormattedMessage
                  id="nav.cancelButton"
                  defaultMessage="Cancel"
                />
              </Button>
            </Grid.Column>
            <Grid.Column colMd6>
              <Button primary fullWidth onClick={handleLogout} id="logoutModal">
                <FormattedMessage
                  id="nav.logoutButton"
                  defaultMessage="Log out"
                />
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal>
    </nav>
  );
};

const DashboardLinks = ({ drawer, navLinks }) => {
  return (
    <ul className={cn(styles.links, { [styles.inDrawer]: drawer })}>
      {navLinks.map((navLink, index) => (
        <li key={index}>
          <Button
            underline
            link
            primary
            className={cn(
              styles.link,
              styles.underline,
              styles.thickUnderline,
              {
                [styles.active]: navLink.isActive,
              }
            )}
            href={navLink.href}
            onClick={navLink.onClick}
          >
            {navLink.label}
          </Button>
        </li>
      ))}
    </ul>
  );
};

Checkout.propTypes = {
  /** The callback function called when the language switch button is clicked */
  setLanguage: PropTypes.func,
  /** Whether or not the user is logged in */
  loggedIn: PropTypes.bool,
  /** Show the dashboard layout */
  dashboard: PropTypes.bool,
  /** URL of the login page */
  loginURL: PropTypes.string,
  /** A callback function called when clicking the log out button */
  onLogout: PropTypes.func,
  /** A callback function called when clicking the Cart button */
  onCartClick: PropTypes.func,
  /** A callback function called when clicking the Logo button */
  onLogoClick: PropTypes.func,
  /** The links to add to the nav */
  navLinks: PropTypes.array,
};

DashboardLinks.propTypes = {
  drawer: PropTypes.bool,
  navLinks: PropTypes.array,
};

Checkout.defaultProps = {
  setLanguage: () => {},
  loggedIn: undefined,
  loginURL: undefined,
  onLogout: undefined,
  onCartClick: () => {},
  onLogoClick: () => {},
  navLinks: [],
};

export default Checkout;
